import {ApolloClient, ApolloLink, HttpLink, InMemoryCache, QueryOptions, ServerError} from "@apollo/client";
import {GRAPHQL} from "./endpoints";
import {AUTHORIZATION, GRAPHQL_ENDPOINT_HEADER} from "./headers";
import {ErrorResponse, onError} from "@apollo/client/link/error";
import {SearchQuery} from "../types/Search";
import TokenStorage from "./token-storage";
import SecurityService from "./security-service";
import SecurityContextStorage from "./security-context-storage";

/**
 * A service which wraps apollo client and can be used for executing queries against the search
 * service.
 *
 * This service is stateful because it initializes and holds an instance of the apollo client.
 * Each new instance will have its own client with its underlying cache. Basically this service is
 * intended to be used only in the search page.
 */
export default class SearchService {

  private client: any;

  constructor() {
    this.init();
  }

  /**
   * Executes query against the configured graphql endpoint using provided
   * query and variables.
   *
   * @param query graphql query
   */
  public query(query: SearchQuery) {
    return this.queryInternal(query, 1);
  }

  private queryInternal(query: SearchQuery, retries: number) {
    let options: QueryOptions = {
      query: query.query,
      variables: query.variables,
      errorPolicy: 'all'
    };

    // when the security is enabled, we need to add auth header to the request
    options = SearchService.addAuthHeaderWhenRequired(options);

    return this.client.query(options).catch((errorResponse: ErrorResponse) => {
      if (SecurityContextStorage.getContext().enabled && retries > 0) {
        const networkError: ServerError | any = errorResponse.networkError;
        if (networkError?.statusCode === 401 && TokenStorage.hasRefreshToken()) {
          retries--;
          // refresh the tokens and retry the request
          return SecurityService.refresh(() => this.queryInternal(query, retries));
        }
      }

      return Promise.reject(this.handleErrorResponse(errorResponse));
    });
  }

  private static addAuthHeaderWhenRequired(options: QueryOptions): QueryOptions {
    if (!SecurityContextStorage.getContext().enabled) {
      return options;
    }

    // add auth header dynamically for every request.
    // Source: https://www.apollographql.com/docs/react/networking/advanced-http-networking/#overriding-options
    return {
      ...options,
      context: {
        headers: {
          [AUTHORIZATION]: `Bearer ${TokenStorage.getAccessToken()}`
        }
      }
    }
  }

  public getClient() {
    return this.client;
  }

  private init() {
    const cache = new InMemoryCache();

    // Making custom initialization of the ApolloClient in order to:
    // - measure and log query execution time
    // - add the custom graphql endpoint header needed by the proxy
    // in order to properly dispatch requests
    // - add generic error handling
    const apolloLinks = [];
    if (process.env.NODE_ENV === 'development') {
      apolloLinks.push(this.createStartTimeLink());
      apolloLinks.push(this.createLogTimeLink());
    }
    apolloLinks.push(this.createErrorHandlerLink());
    apolloLinks.push(SearchService.createHttpLink());
    const link = ApolloLink.from(apolloLinks);

    this.client = new ApolloClient({
      cache: cache,
      link: link,
      defaultOptions: {
        query: {
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        }
      }
    });
  }

  private createStartTimeLink() {
    return new ApolloLink((operation, forward) => {
      operation.setContext({start: new Date()});
      return forward(operation);
    });
  }

  private createLogTimeLink() {
    return new ApolloLink((operation, forward) => {
      return forward(operation).map(data => {
        // data from a previous link
        const date: any = new Date();
        const time: any = date - operation.getContext().start;
        console.log(`Operation [${operation.operationName}] took ${time}ms to complete`);
        return data;
      });
    });
  }

  private static createHttpLink() {
    return new HttpLink({
      uri: GRAPHQL,
      headers: {
        [GRAPHQL_ENDPOINT_HEADER]: 'search'
      }
    });
  }

  private createErrorHandlerLink() {
    return onError((errorResponse: ErrorResponse) => {
      const errors = this.handleErrorResponse(errorResponse);
      console.error(`Apollo request errors: ${errors}`);
    });
  }

  private handleErrorResponse(errorResponse: any): Array<string> {
    const graphQLErrors: any = errorResponse.graphQLErrors;
    const networkError: ServerError = errorResponse.networkError;
    const errors = [];
    if (graphQLErrors) {
      const errorMessages = graphQLErrors.map((err: any) => {
        const message = err.message;
        const locations = err.locations;
        const path = err.path;
        return `[GraphQL error]: Message: ${message}, Locations: ${JSON.stringify(locations)}, Path: ${path}`;
      });
      errors.push(...errorMessages);
    }
    if (networkError) {
      const msg = networkError.result && networkError.result.message;
      errors.push(`[Network error]: ${msg}`);
    }
    return errors;
  }
}

